<template>
  <div class="subscription-mobile" :class="{ 'is-embedded': embedded }">
    <header class="member-header">
      <div class="member-profile">
        <div class="member-avatar-column">
          <el-avatar :size="embedded ? 42 : 48" :src="userAvatar" />
          <span v-if="membershipExpiryText" class="member-expiry">{{ membershipExpiryText }}</span>
        </div>
        <div class="member-identity">
          <div class="member-name-row">
            <strong class="member-name">{{ userNickname }}</strong>
            <el-tag size="small" effect="plain" type="primary">{{ currentMembershipLabel }}</el-tag>
          </div>
          <el-tooltip v-if="userEmail" :content="userEmail" placement="bottom" :show-after="500">
            <span class="member-email">{{ userEmail }}</span>
          </el-tooltip>
          <span v-else class="member-email">{{ $t('views.account_center.purchase_account_fallback') }}</span>
        </div>
      </div>

      <div class="member-header-actions">
        <div class="points-summary">
          <div class="points-summary-total">
            <span>{{ $t('views.account_center.total_points') }}</span>
            <strong>{{ formatNumber(pointsData.totalPoints) }}</strong>
          </div>
        </div>
        <el-button class="fuel-button" type="primary" plain @click="$emit('toggle-fuel')">
          <el-icon><Coin /></el-icon>
          {{ $t('views.account_center.purchase_fuel_packages') }}
        </el-button>
        <div class="points-summary-breakdown">
          <span><small>{{ $t('views.account_center.subscription_points') }}</small><strong>{{ formatPointRange(pointsData.subPoints, pointsData.subTotal) }}</strong></span>
          <span><small>{{ $t('views.account_center.purchased_points') }}</small><strong>{{ formatPointRange(pointsData.buyPoints, pointsData.buyTotal) }}</strong></span>
          <span><small>{{ $t('views.account_center.activity_points') }}</small><strong>{{ formatPointRange(pointsData.actPoints, pointsData.actTotal) }}</strong></span>
        </div>
        <el-button
          v-if="!embedded"
          class="purchase-close"
          circle
          text
          :aria-label="$t('common.close')"
          @click="$emit('close')"
        >
          <el-icon><Close /></el-icon>
        </el-button>
      </div>
    </header>

    <section class="purchase-main">
      <el-alert
        v-if="!canManageSubscription"
        class="subscription-owner-notice"
        type="info"
        :closable="false"
        :title="$t('views.account_center.purchase_owner_only')"
      />
      <div v-if="isLoading" class="subscription-loading" aria-busy="true">
        <el-skeleton animated :rows="8" />
      </div>

      <div v-else-if="versionPlans.length" class="subscription-layout">
        <div class="version-tabs" role="tablist" :aria-label="$t('views.account_center.purchase_version_label')">
          <button
            v-for="plan in versionPlans"
            :key="plan.ver_id"
            type="button"
            role="tab"
            :aria-selected="selectedPlan === plan.ver_id"
            :class="{ active: selectedPlan === plan.ver_id }"
            @click="$emit('select-plan', plan.ver_id)"
          >
            <span>{{ plan.ver_name }}</span>
          </button>
        </div>

        <aside class="benefits-panel">
          <h2>{{ selectedPlanData?.ver_name }}</h2>
          <p class="benefits-description">{{ selectedPlanData?.desc }}</p>
          <div class="benefits-rule" />
          <ul class="benefits-list">
            <li v-for="(feature, index) in visibleFeatures(selectedPlanData)" :key="index">
              <span class="benefit-index">{{ String(index + 1).padStart(2, '0') }}</span>
              <span>{{ feature }}</span>
            </li>
          </ul>
        </aside>

        <div class="subscription-selection">
          <div class="period-heading">
            <span>{{ $t('views.account_center.purchase_billing_label') }}</span>
            <small>{{ $t('views.account_center.purchase_period_hint') }}</small>
          </div>
          <div
            class="period-options"
            role="radiogroup"
            :aria-label="$t('views.account_center.purchase_billing_label')"
          >
            <button
              v-for="pricing in availablePricingPlans"
              :key="pricing.plan_id"
              type="button"
              role="radio"
              :aria-checked="selectedPeriodType === Number(pricing.period_type)"
              class="period-card"
              :class="{ active: selectedPeriodType === Number(pricing.period_type), recommended: pricing.recommend === 1 }"
              @click="$emit('select-period', Number(pricing.period_type))"
            >
              <span v-if="pricing.recommend === 1" class="period-badge">{{ $t('views.account_center.most_popular') }}</span>
              <span class="period-name">{{ periodName(pricing.period_type) }}</span>
              <strong>{{ formatPackagePrice(pricing.price, pricing.currency) }}</strong>
              <span class="period-subline">{{ getPricingSubline(pricing) }}</span>
              <del v-if="pricing.original_price && Number(pricing.original_price) > Number(pricing.price)">{{ formatPackagePrice(pricing.original_price, pricing.currency) }}</del>
            </button>
          </div>

          <section
            v-if="showPriceQuote"
            class="purchase-price-quote"
            aria-live="polite"
            :aria-busy="priceQuoteLoading"
          >
            <dl :aria-hidden="priceQuoteError">
              <div>
                <dt>{{ $t('views.account_center.purchase_plan_price') }}</dt>
                <dd><span v-if="hasCurrentPriceQuote && !priceQuoteLoading">{{ formatPackagePrice(priceQuote.price, priceQuote.currency) }}</span><el-skeleton-item v-else-if="!priceQuoteError" variant="text" class="quote-amount-skeleton" aria-hidden="true" /><span v-else>—</span></dd>
              </div>
              <div>
                <dt>{{ $t('views.account_center.purchase_credit') }}</dt>
                <dd><span v-if="hasCurrentPriceQuote && !priceQuoteLoading">-{{ formatPackagePrice(Number(priceQuote.discount).toFixed(2), priceQuote.currency) }}</span><el-skeleton-item v-else-if="!priceQuoteError" variant="text" class="quote-amount-skeleton" aria-hidden="true" /><span v-else>—</span></dd>
              </div>
              <div class="price-quote-total">
                <dt>{{ $t('views.account_center.purchase_payable') }}</dt>
                <dd><span v-if="hasCurrentPriceQuote && !priceQuoteLoading">{{ formatPackagePrice(priceQuote.amount, priceQuote.currency) }}</span><el-skeleton-item v-else-if="!priceQuoteError" variant="text" class="quote-amount-skeleton" aria-hidden="true" /><span v-else>—</span></dd>
              </div>
            </dl>
            <small :aria-hidden="priceQuoteError">{{ $t('views.account_center.purchase_quote_note') }}</small>
            <div v-if="priceQuoteError" class="price-quote-error">
              <span>{{ $t('views.account_center.purchase_quote_failed') }}</span>
              <el-button text type="primary" @click="$emit('retry-price-quote')">{{ $t('views.account_center.purchase_quote_retry') }}</el-button>
            </div>
          </section>

          <div v-if="selectedPricingPlan && canManageSubscription" class="purchase-summary">
            <div>
              <span>{{ $t('views.account_center.purchase_selected_plan') }}</span>
              <strong>{{ selectedPlanData.ver_name }} · {{ periodName(selectedPricingPlan.period_type) }}</strong>
            </div>
            <el-button
              type="primary"
              :disabled="!canSubmitPlanPurchase"
              :loading="isCreatingOrder || priceQuoteLoading"
              :aria-busy="isCreatingOrder || priceQuoteLoading"
              @click="$emit('purchase-plan', selectedPlanData)"
            >
              {{ purchaseActionLabel }}
            </el-button>
          </div>

        </div>
      </div>

      <section v-else class="membership-empty-state">
        <p>{{ $t('views.account_center.purchase_no_matching_plan') }}</p>
        <el-button type="primary" @click="$emit('toggle-fuel')"><el-icon><Coin /></el-icon>{{ $t('views.account_center.purchase_fuel_packages') }}</el-button>
      </section>
      <footer v-if="!isLoading" class="purchase-footnote">
        <el-icon><Lock /></el-icon>
        <span>{{ purchaseFootnote }}</span>
      </footer>
    </section>
    <el-drawer
      :model-value="fuelPackagesVisible"
      class="subscription-mobile fuel-packages-drawer"
      direction="btt"
      size="auto"
      :title="$t('views.account_center.purchase_fuel_packages')"
      append-to-body
      @update:model-value="closeFuelDrawer"
    >
        <div class="fuel-packages-grid">
          <template v-if="fuelPackagesLoading">
            <el-skeleton v-for="item in 3" :key="item" animated class="fuel-package-skeleton"><template #template><el-skeleton-item variant="h3" /><el-skeleton-item variant="text" /><el-skeleton-item variant="button" /></template></el-skeleton>
          </template>
          <template v-else-if="fuelPackages.length">
            <article v-for="pkg in fuelPackages" :key="pkg.id" class="fuel-package-card">
              <el-tag v-if="pkg.is_recommend === 1" size="small" type="primary" effect="light">{{ $t('views.account_center.recommended') }}</el-tag>
              <div class="fuel-package-title"><el-icon><Coin /></el-icon>{{ pkg.package_name }}</div>
              <p>{{ pkg.description }}</p>
              <strong>{{ formatPackagePrice(pkg.price, pkg.currency) }}</strong>
              <el-button type="primary" :disabled="isDomestic && paymentChannelsLoading" :loading="fuelPurchaseLoading === pkg.id" @click="$emit('purchase-fuel', pkg)">{{ $t('views.account_center.buy_now') }}</el-button>
            </article>
          </template>
          <el-empty v-else :description="$t('views.account_center.purchase_no_fuel_packages')" />
        </div>
    </el-drawer>
  </div>
</template>

<script>
import { Close, Coin, Lock } from '@element-plus/icons-vue'

export default {
  name: 'MobileSubscriptionPurchase',
  components: { Close, Coin, Lock },
  props: {
    userAvatar: { type: String, default: '' },
    userNickname: { type: String, default: '' },
    userEmail: { type: String, default: '' },
    membershipExpiryText: { type: String, default: '' },
    currentMembershipLabel: { type: String, default: '' },
    purchaseFootnote: { type: String, default: '' },
    purchaseActionLabel: { type: String, default: '' },
    versionPlans: { type: Array, default: () => [] },
    availablePricingPlans: { type: Array, default: () => [] },
    fuelPackages: { type: Array, default: () => [] },
    embedded: Boolean,
    isLoading: Boolean,
    isCreatingOrder: Boolean,
    canManageSubscription: Boolean,
    canSubmitPlanPurchase: Boolean,
    showPriceQuote: Boolean,
    priceQuote: { type: Object, default: null },
    priceQuoteLoading: Boolean,
    priceQuoteError: Boolean,
    hasCurrentPriceQuote: Boolean,
    isDomestic: Boolean,
    paymentChannelsLoading: Boolean,
    fuelPackagesVisible: Boolean,
    fuelPackagesLoading: Boolean,
    formatNumber: { type: Function, required: true },
    formatPointRange: { type: Function, required: true },
    visibleFeatures: { type: Function, required: true },
    periodName: { type: Function, required: true },
    formatPackagePrice: { type: Function, required: true },
    getPricingSubline: { type: Function, required: true },
    pointsData: { type: Object, required: true },
    selectedPlanData: { type: Object, default: null },
    selectedPricingPlan: { type: Object, default: null },
    selectedPlan: { type: [String, Number], default: null },
    selectedPeriodType: { type: Number, required: true },
    fuelPurchaseLoading: { type: [String, Number], default: null }
  },
  emits: ['close', 'toggle-fuel', 'select-plan', 'select-period', 'purchase-plan', 'purchase-fuel', 'retry-price-quote'],
  methods: {
    closeFuelDrawer(visible) {
      if (!visible && this.fuelPackagesVisible) this.$emit('toggle-fuel')
    }
  }
}
</script>

<style scoped lang="scss">
@use '../styles/subscription_purchase' as mobile;
@include mobile.content;

.subscription-mobile {
  .subscription-owner-notice { margin-bottom: 16px; }
  .purchase-price-quote { position: relative; margin-top: 16px; padding: 16px; border: 1px solid var(--purchase-border); border-radius: 8px; color: var(--purchase-regular); font-size: 14px; }
  .purchase-price-quote dl { display: flex; flex-direction: column; gap: 12px; margin: 0; }
  .purchase-price-quote dl > div { display: flex; justify-content: space-between; gap: 12px; min-height: 20px; line-height: 20px; }
  .purchase-price-quote dd { display: flex; align-items: center; flex-shrink: 0; max-width: 50%; margin: 0; font-variant-numeric: tabular-nums; }
  .quote-amount-skeleton { width: 72px; max-width: 100%; height: 16px; }
  .price-quote-total { padding-top: 12px; border-top: 1px solid var(--purchase-border); color: var(--purchase-text); font-weight: 600; }
  .purchase-price-quote small { display: block; margin-top: 12px; color: var(--purchase-muted); line-height: 1.5; }
  // Overlay the reserved rows so a failed request and retry do not move the submit button.
  .price-quote-error { position: absolute; inset: 0; padding: 16px; border-radius: inherit; background: var(--purchase-surface); display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 8px; }
  .price-quote-error .el-button { min-height: 44px; }
}
.fuel-packages-drawer {
  --purchase-primary: var(--agst-color-primary, #2d66f0);
  --purchase-text: var(--agst-text-color-primary, #303133);
  --purchase-muted: var(--agst-text-color-secondary, #909399);
  --purchase-border: var(--agst-border-color-lighter, #ebeef5);
  --purchase-surface: var(--agst-bg-color, #fff);
  max-height: min(78dvh, 640px);
  padding-bottom: env(safe-area-inset-bottom, 0px);
  :deep(.el-drawer__header) { margin: 0; padding: 16px 20px 8px; }
  :deep(.el-drawer__title) { font-size: 17px; font-weight: 600; }
  :deep(.el-drawer__body) { min-height: 0; padding: 8px 16px 16px; overflow-y: auto; }
  .fuel-packages-grid { margin-top: 0; }
  .fuel-package-card { gap: 8px; padding: 12px; }
  .fuel-package-card .el-tag { align-self: flex-start; }
  .fuel-package-title { font-size: 17px; }
  .fuel-package-card p { font-size: 14px; line-height: 1.5; }
  .fuel-package-card strong { font-size: 21px; }
  .fuel-package-card .el-button { margin-top: 2px; }
}
:global(.fuel-packages-drawer .el-drawer__header) { margin-bottom: 0; }
</style>
